import { api, ApiError } from "@/lib/api-client";
import { isServerTimeZone } from "@/utils/analytics-range";

// Client for the `analytics` edge function. All aggregation, range and
// time-zone logic lives server-side (supabase/functions/analytics); this file
// only mirrors the wire types. Keep it in sync with that function's types.ts.

export type AnalyticsPreset = "today" | "7d" | "30d";
/** Preset, or an inclusive custom range of local calendar days (YYYY-MM-DD). */
export type AnalyticsRange = AnalyticsPreset | { from: string; to: string };

export interface CallAnalyticsParams {
  workspaceId: string;
  range: AnalyticsRange;
  callType?: string | null;
  agentId?: string | null;
  /** IANA zone the calendar days are interpreted in; defaults to the browser's. */
  tz?: string;
}

export interface SeriesPoint {
  key: string; // bucket start day, YYYY-MM-DD
  label: string; // e.g. "Aug 5"
  calls: number;
  connected: number;
  cost: number; // dollars
}

export interface Distribution {
  name: string;
  value: number;
}

export interface CallAnalytics {
  range: { from: string; to: string; days: number; bucket: "day" | "week"; tz: string };
  total_calls: number;
  connected_calls: number; // duration > 0
  connect_rate: number; // 0–1
  total_cost: number; // dollars
  avg_duration: number; // seconds, over connected calls
  cost_per_call: number; // dollars
  series: SeriesPoint[];
  call_types: Distribution[];
  statuses: Distribution[];
}

/** The zone the reader's "today" is in, when the function takes it; otherwise UTC, the function's own default. */
export const browserTimeZone = (): string => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  return isServerTimeZone(zone) ? zone : "UTC";
};

/**
 * Whether the function refused the zone itself rather than the request: a 422 naming the `tz`
 * field, in its "field: message" form. It answers that for a zone newer than its database's
 * time-zone data ("… is not supported yet"), which nothing in the browser can know of.
 */
const isZoneRefusal = (error: unknown) =>
  error instanceof ApiError && error.status === 422 && error.message.startsWith("tz:");

/**
 * `POST /analytics/calls`. A zone the function cannot cut days in yet is asked again in UTC, so
 * the page still charts — the report names the zone it was cut in, and the page shows it. Any
 * other refusal rejects with the server's own sentence.
 */
export const getCallAnalytics = async ({ workspaceId, range, callType = null, agentId = null, tz }: CallAnalyticsParams) => {
  const reportIn = (zone: string) =>
    api.post<CallAnalytics>("/analytics/calls", {
      workspace_id: workspaceId,
      range,
      call_type: callType,
      agent_id: agentId,
      tz: zone,
    });
  const zone = tz ?? browserTimeZone();
  try {
    return await reportIn(zone);
  } catch (error) {
    if (zone === "UTC" || !isZoneRefusal(error)) throw error;
    return reportIn("UTC");
  }
};
